/**
 * One recording in, its words out — the upload half of voice input.
 *
 * Direct to the voice service (same origin, Caddy `/voice/*`), never through the
 * relay or the BFF: the owner's ruling is that audio does not ride the BFF, and a
 * clip is a bounded blob a plain POST carries whole. The blob's own type travels as
 * the part's content type (Chrome/Android: audio/webm;codecs=opus, iOS: audio/mp4 —
 * the service accepts both as-is), and the measured duration rides along because a
 * WebM from MediaRecorder carries none and the service's quota needs one.
 *
 * Contract (avengents-voice-service/docs/frontend-mic-button.md §2). Every error body
 * is {error, message} with `message` written for the person; it is shown verbatim.
 * A 401 means the token died — refresh once, retry once, then give up honestly.
 *
 * Deliberately NOT here: the recorder. This file takes a Blob and knows nothing about
 * where it came from, so the VAD phase (segments → 16 kHz WAV → stream=1) swaps the
 * source without touching the upload.
 */
import { noteClip } from '../billing/plan'
import { ensureVoiceToken } from './voiceToken'

export class VoiceError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    /** The refusal's other fields — a 429 `quota` carries plan/used_s/limit_s/resets_at. */
    public readonly details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'VoiceError'
  }
}

export type Transcript = {
  text: string
  language: string | null
  durationS: number
  model: string
}

const GENERIC = 'Transcription failed. Try again.'

/** A filename the service can name the container by; it never reads it for anything else. */
function clipName(type: string): string {
  if (/mp4|m4a|aac/.test(type)) return 'clip.mp4'
  if (/ogg/.test(type)) return 'clip.ogg'
  if (/wav/.test(type)) return 'clip.wav'
  return 'clip.webm'
}

async function post(token: string, blob: Blob, durationMs: number): Promise<Response> {
  const form = new FormData()
  // A File rather than append's filename argument: the name then travels with the
  // part on every engine (jsdom drops the third argument; some webviews have too).
  form.append('file', new File([blob], clipName(blob.type), { type: blob.type }))
  form.append('duration_ms', String(Math.max(0, Math.round(durationMs))))
  // The bearer IS the credential: no cookie rides this request, so it is not a
  // cookie-authenticated mutation and needs no CSRF token (csrfCoverage.test names
  // this lane, like chat v1's).
  return fetch('/voice/v1/transcribe', {
    method: 'POST',
    credentials: 'omit',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })
}

async function failure(res: Response): Promise<VoiceError> {
  let code = `http_${res.status}`
  let message = GENERIC
  let details: Record<string, unknown> = {}
  try {
    const body = (await res.json()) as { error?: unknown; message?: unknown }
    if (typeof body.error === 'string' && body.error) code = body.error
    if (typeof body.message === 'string' && body.message.trim()) message = body.message
    if (body && typeof body === 'object') details = body as Record<string, unknown>
  } catch {
    // A non-JSON refusal (a proxy page, an empty body): the generic sentence stands.
  }
  return new VoiceError(code, message, res.status, details)
}

export async function transcribeBlob(blob: Blob, durationMs: number): Promise<Transcript> {
  let token = await ensureVoiceToken()
  if (!token) throw new VoiceError('unauthorized', 'Sign in to use voice input.', 401)

  let res: Response
  try {
    res = await post(token, blob, durationMs)
    if (res.status === 401) {
      // The token died in flight (or the cache outlived it): one fresh mint, one retry.
      token = await ensureVoiceToken(true)
      if (!token) throw new VoiceError('unauthorized', 'Sign in to use voice input.', 401)
      res = await post(token, blob, durationMs)
    }
  } catch (e) {
    if (e instanceof VoiceError) throw e
    throw new VoiceError('network', GENERIC, 0)
  }
  if (res.status === 401) throw new VoiceError('unauthorized', 'Sign in to use voice input.', 401)
  if (!res.ok) throw await failure(res)

  const body = (await res.json().catch(() => ({}))) as {
    text?: unknown
    language?: unknown
    duration_s?: unknown
    model?: unknown
  }
  const out: Transcript = {
    text: typeof body.text === 'string' ? body.text : '',
    language: typeof body.language === 'string' ? body.language : null,
    durationS: typeof body.duration_s === 'number' ? body.duration_s : 0,
    model: typeof body.model === 'string' ? body.model : '',
  }
  // The service metered it; charge today's allowance locally until the next read.
  noteClip(out.durationS || durationMs / 1000)
  return out
}
